
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuth } from '@/contexts/auth';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import TranslatedText from '@/TranslatedText';

interface AhavexerLoginFormProps {
  onSuccess?: () => void;
}

const ahavexerFormSchema = z.object({
  emailOrAhavexId: z.string().min(1, { message: 'Email or Ahavexer ID is required' }),
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
});

type AhavexerFormData = z.infer<typeof ahavexerFormSchema>;

const AhavexerLoginForm: React.FC<AhavexerLoginFormProps> = ({ onSuccess }) => {
  const { loginMember, userType, setUserType } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showNotAhavexerDialog, setShowNotAhavexerDialog] = useState(false);
  const navigate = useNavigate();
  
  // Get admin email from environment variable
  const adminEmail = import.meta.env.VITE_ADMIN_EMAIL || 'ahavexadmin@gmail.com';

  const form = useForm<AhavexerFormData>({
    resolver: zodResolver(ahavexerFormSchema),
    defaultValues: {
      emailOrAhavexId: '',
      password: '',
    },
  });

  const onSubmit = async (data: AhavexerFormData) => {
    setIsLoading(true);
    try {
      // Check if the email is the admin email before attempting to login
      const isAdminLogin = data.emailOrAhavexId === adminEmail;
      
      if (isAdminLogin) {
        // If it's admin email, login and then explicitly set userType to admin
        await loginMember(data.emailOrAhavexId, data.password);
        setUserType('admin');
        toast.success('Successfully logged in as admin');
        navigate('/admin-dashboard');
        if (onSuccess) onSuccess();
      } else {
        // For regular ahavexer login
        await loginMember(data.emailOrAhavexId, data.password);
        
        // Check if login set the user type to admin
        if (userType === 'admin') {
          toast.success('Successfully logged in as admin');
          navigate('/admin-dashboard');
        } else {
          toast.success('Successfully logged in as ahavexer');
        }
        
        if (onSuccess) onSuccess();
      }
    } catch (error: any) {
      console.error(error);
      
      // Check if the error is related to the user not being an ahavexer
      if (error.message?.includes('not an ahavexer')) {
        setShowNotAhavexerDialog(true);
      } else {
        toast.error('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ScrollArea className="h-full max-h-[80vh]">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-4">
            <FormField
              control={form.control}
              name="emailOrAhavexId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel><TranslatedText text="Email or Ahavexer ID" /></FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your email or Ahavexer ID" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel><TranslatedText text="Password" /></FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="Enter your password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? <TranslatedText text="Logging in..." /> : <TranslatedText text="Login" />}
            </Button>
          </form>
        </Form>
      </ScrollArea>

      {/* Dialog for not an ahavexer error */}
      <Dialog open={showNotAhavexerDialog} onOpenChange={setShowNotAhavexerDialog}>
        <DialogContent>
          <DialogTitle><TranslatedText text="Customer Account Detected" /></DialogTitle>
          <DialogDescription>
            <TranslatedText text="Your account appears to be a customer account, not an Ahavexer account. Please login through the customer login instead." />
          </DialogDescription>
          <div className="flex justify-end mt-4">
            <Button onClick={() => setShowNotAhavexerDialog(false)}>
              <TranslatedText text="Close" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AhavexerLoginForm;
